import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";

import FormSelectionField from "../../components/formFields/FormSelectionField";

import { STATUSARRAY } from "../../_contstants/common";
import {
  ServiceImageUpload,
  createService,
  getServiceByID,
  updateService,
} from "./services/service.services";
import { clearServiceDetails } from "./serviceSlice";
import FormRichTextField from "../../components/formFields/FormRichTextField";

import FormTextArea from "../../components/formFields/FormTextArea";
import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";
import { slugify } from "../../_helper/helper";
import ServiceImageArray from "./ServiceImageArray";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";

const CreateService: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const ServiceID = location.state?.ServiceID;
  const ServiceDetail = useAppSelector((state) => state.service.ServiceDetail);
  const loading = useAppSelector((state) => state.service.loading);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (ServiceID) {
      dispatch(getServiceByID(ServiceID));
    }
    return () => {
      dispatch(clearServiceDetails());
    };
  }, [ServiceID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ServiceDetail]);

  const handleSubmit = async (values: any) => {
    const formData = {
      ServiceName: values?.ServiceName ? values?.ServiceName : "",
      ServiceURL: values?.ServiceURL ? values?.ServiceURL : "",
      ShortDescription: values?.ShortDescription
        ? values?.ShortDescription
        : "",
      LongDescription: values?.LongDescription ? values?.LongDescription : "",
      AboutService: values?.AboutService ? values?.AboutService : "",
      ServiceDetails: values?.ServiceDetails ? values?.ServiceDetails : "",
      MetaTitle: values?.MetaTitle ? values?.MetaTitle : "",
      MetaKeywords: values?.MetaKeywords ? values?.MetaKeywords : "",
      MetaDescription: values?.MetaDescription ? values?.MetaDescription : "",
      SequenceNo: values?.SequenceNo ? +values?.SequenceNo : null,
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const MainServiceImage =
      values?.MainServiceImage &&
      typeof values?.MainServiceImage !== "string" &&
      Array.isArray(values?.MainServiceImage) &&
      values?.MainServiceImage?.length > 0
        ? values.MainServiceImage[0].getRawFile()
        : "";
    const BannerImage =
      values?.BannerImage &&
      typeof values?.BannerImage !== "string" &&
      Array.isArray(values?.BannerImage) &&
      values?.BannerImage?.length > 0
        ? values.BannerImage[0].getRawFile()
        : "";
    const ContentImage =
      values?.ContentImage &&
      typeof values?.ContentImage !== "string" &&
      Array.isArray(values?.ContentImage) &&
      values?.ContentImage?.length > 0
        ? values.ContentImage[0].getRawFile()
        : "";

    try {
      if (ServiceID) {
        const editPayload = {
          ...formData,
          ID: ServiceID,
        };

        const response = await dispatch(updateService(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values.MainServiceImage &&
              typeof values.MainServiceImage !== "string") ||
            (values.BannerImage && typeof values.BannerImage !== "string") ||
            (values.ContentImage && typeof values.ContentImage !== "string")
          ) {
            ImageData.append("MainServiceImage", MainServiceImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("ContentImage", ContentImage);
            ImageData.append("ID", ServiceID);
            await dispatch(ServiceImageUpload(ImageData));
          }
          navigate("/service");
        }
      } else {
        const response = await dispatch(createService(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values.MainServiceImage &&
              typeof values.MainServiceImage !== "string") ||
              (values.BannerImage && typeof values.BannerImage !== "string") ||
              (values.ContentImage &&
                typeof values.ContentImage !== "string")) &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("MainServiceImage", MainServiceImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("ContentImage", ContentImage);
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(ServiceImageUpload(ImageData));
          }
          navigate("/service");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("ServiceURL", { value: slug });
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={ServiceDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {ServiceID ? "Update Service" : "Add Service"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="ServiceName"
                      label="Service"
                      placeholder="Service"
                      component={FormTextField}
                      onChange={(e) => handleCreateSlug(e, formRenderProps)}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="ServiceURL"
                      label="Service URL"
                      placeholder="Service URL"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="MetaTitle"
                      label="Meta Title"
                      placeholder="Meta Title"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="MetaKeywords"
                      label="Meta Keywords"
                      placeholder="Meta Keywords"
                      component={FormTextField}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="MetaDescription"
                      label="Meta Description"
                      placeholder="Meta Description"
                      component={FormTextArea}
                      style={{ height: 130 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="ShortDescription"
                      label="Short Description"
                      placeholder="Short Description"
                      component={FormRichTextField}
                      astrike={true}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="LongDescription"
                      label="Long Description"
                      component={FormRichTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="AboutService"
                      label="About Service"
                      component={FormRichTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="ServiceDetails"
                      label="Service Details"
                      component={FormRichTextField}
                    />
                  </GridLayoutItem>

                  {/* <GridLayoutItem></GridLayoutItem> */}
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: 20,
                      display: "flex",
                      width: "100%",
                      gap: "10px",
                    }}
                  >
                    <Field
                      justifyContent="start"
                      name={`MainServiceImage`}
                      label="Service Image"
                      type="file"
                      accept="image/*"
                      style={{ width: 100 }}
                      alternateName="Image"
                      component={FormImageUploadMorden}
                    />
                    <Field
                      justifyContent="start"
                      name={`BannerImage`}
                      label="Service Banner Image"
                      type="file"
                      accept="image/*"
                      alternateName="Service Banner Image"
                      component={FormImageUploadMorden}
                    />
                    <Field
                      justifyContent="start"
                      name={`ContentImage`}
                      label="Content Image"
                      type="file"
                      accept="image/*"
                      alternateName="Content Image"
                      component={FormImageUploadMorden}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperClassName="right-aligned-field"
                      name="SequenceNo"
                      label="SequenceNo"
                      placeholder="0"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={ServiceID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/service")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>

              {ServiceID && (
                <ShadowCard style={{ marginTop: 10, marginBottom: 40 }}>
                  <GridLayout
                    style={{ marginTop: 30 }}
                    gap={{ rows: 5, cols: 10 }}
                  >
                    <GridLayoutItem>
                      <FieldArray
                        formRenderProps={formRenderProps}
                        component={ServiceImageArray}
                        name="ServicesImageDetails"
                      />
                    </GridLayoutItem>
                  </GridLayout>
                </ShadowCard>
              )}
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateService;
